import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { filter, fromEvent, interval, takeUntil } from 'rxjs'
import { SimpleWindowRefService } from '../../services'
import { DestroySubscribers } from '../../util'
import { IconComponent } from '../icon'

export type LivenessState = 'live' | 'disconnected' | 'paused'

/**
 * Emits refresh every "updateInterval" automatically
 * Also emits if the user presses the "refresh" button
 *
 * Note: It is the responsibility of the parent to let this component
 * know if the refresh succeeded or not.
 *
 * myApiRequest
 *     .pipe(tapData(() => liveActionBar.isSuccess = true))
 *     .pipe(tapFailure(() => liveActionBar.isSuccess = false)))
 */
@Component({
  selector: 'ftr-live-action-bar',
  templateUrl: './live-action-bar.component.html',
  styleUrls: ['./live-action-bar.component.css'],
  standalone: true,
  imports: [CommonModule, IconComponent],
})
export class LiveActionBarComponent extends DestroySubscribers implements OnInit {
  @Input()
  updateIntervalMs: number

  /**
   * Whether scrolling the page should pause automatic updates.
   * Useful when you have a large list and dont want the user to lose their place
   */
  @Input()
  pauseUpdatesOnScroll = false

  /**
   * Was the last emission a success
   */
  @Input()
  isSuccess: boolean

  @Output()
  refresh: EventEmitter<void> = new EventEmitter<void>()

  @Input() isAutoUpdateEnabled: boolean = true
  @Output() isAutoUpdateEnabledChange = new EventEmitter<boolean>()

  constructor(private readonly windowRef: SimpleWindowRefService) {
    super()
  }

  get liveness(): LivenessState {
    if (!this.isSuccess) {
      return 'disconnected'
    }

    if (!this.isAutoUpdateEnabled) {
      return 'paused'
    }
    return 'live'
  }

  toggleAutoUpdate(): void {
    this.isAutoUpdateEnabled = !this.isAutoUpdateEnabled
  }

  ngOnInit(): void {
    interval(this.updateIntervalMs)
      .pipe(filter(() => this.isAutoUpdateEnabled))
      .pipe(takeUntil(this.finalize))
      .subscribe(() => this.refresh.emit())

    if (this.pauseUpdatesOnScroll) {
      fromEvent(this.windowRef.document(), 'scroll')
        .pipe(takeUntil(this.finalize))
        .subscribe(() => (this.isAutoUpdateEnabled = false))
    }
  }
}
